import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowDown,
  Check,
  FileText,
  Inbox,
  Banknote,
  RefreshCw,
  CreditCard,
  Store,
  Gavel,
  Route,
  BarChart3,
} from "lucide-react";
import FondeaLogo from "@/components/fondea/FondeaLogo";
import { useLanguage } from "@/contexts/LanguageContext";
import { FONDEA_HREF, FONDEA_WAITLIST_HREF, WHITE_LABEL_HREF, getHomeV2 } from "@/content/home-v2";

const featureIcons = [FileText, Inbox, Banknote, RefreshCw, CreditCard, Store, Gavel, Route, BarChart3];

const Eyebrow = ({ children }: { children: string }): JSX.Element => (
  <p className="mb-3 font-mono text-xs uppercase tracking-[0.25em] text-primary">[ {children} ]</p>
);

/** "Where is your need today?" — two clearly separated audiences. */
export const NeedsSplit = (): JSX.Element => {
  const { language } = useLanguage();
  const c = getHomeV2(language).needs;
  return (
    <section className="section-padding section-band-alt">
      <div className="enterprise-container">
        <h2 className="section-title mb-12 text-foreground">{c.title}</h2>
        <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          <div className="rounded-3xl border border-primary/30 bg-card p-8 md:p-10">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary">{c.fondea.tag}</span>
            <h3 className="mt-4 font-display text-2xl font-semibold text-foreground md:text-3xl">{c.fondea.title}</h3>
            <p className="mt-4 text-muted-foreground">{c.fondea.body}</p>
            <ul className="mt-6 grid gap-2 sm:grid-cols-2">
              {c.fondea.items.map((item) => (
                <li key={item} className="flex gap-2 text-sm text-foreground">
                  <Check className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
            <Link to={FONDEA_HREF} className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">
              {c.fondea.cta} <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </div>
          <div className="rounded-3xl border border-border bg-card/60 p-8">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{c.whiteLabel.tag}</span>
            <h3 className="mt-4 font-display text-xl font-semibold text-foreground md:text-2xl">{c.whiteLabel.title}</h3>
            <p className="mt-4 text-sm text-muted-foreground">{c.whiteLabel.body}</p>
            <ul className="mt-6 space-y-2">
              {c.whiteLabel.items.map((item) => (
                <li key={item} className="flex gap-2 text-sm text-muted-foreground">
                  <span className="mt-2 size-1 shrink-0 rounded-full bg-muted-foreground" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
            <Link to={WHITE_LABEL_HREF} className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
              {c.whiteLabel.cta} <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

/** Main FONDEA block: operation flow + cycle + capabilities + 5 steps + funders. */
export const FondeaShowcase = (): JSX.Element => {
  const { language } = useLanguage();
  const all = getHomeV2(language);
  const f = all.fondeaFlow;
  const feats = all.fondeaFeatures;
  const steps = all.fondeaSteps;
  const fund = all.funders;

  return (
    <>
      <section id="fondea" className="section-padding section-band-base">
        <div className="enterprise-container">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <FondeaLogo className="mb-6 h-10 w-auto md:h-12" />
              <h2 className="font-display text-4xl font-semibold tracking-tight text-foreground md:text-5xl">{f.title}</h2>
              <p className="mt-5 max-w-xl text-lg text-muted-foreground">{f.subtitle}</p>
              <p className="mt-10 font-display text-2xl font-semibold text-primary">{f.slogan}</p>
              <p className="mt-1 text-muted-foreground">{f.sloganSub}</p>
            </div>
            <ol className="mx-auto flex w-full max-w-sm flex-col items-center" aria-label={f.title}>
              {f.chain.map((node, i) => {
                const isCore = i === 3;
                return (
                  <li key={node} className="flex w-full flex-col items-center">
                    <span
                      className={`w-full rounded-2xl border px-5 py-3.5 text-center text-sm font-semibold uppercase tracking-wider ${
                        isCore
                          ? "border-primary bg-primary text-primary-foreground shadow-lg"
                          : "border-border bg-card text-foreground"
                      }`}
                    >
                      {node}
                    </span>
                    {i < f.chain.length - 1 && <ArrowDown className="my-2 size-4 text-primary" aria-hidden="true" />}
                  </li>
                );
              })}
            </ol>
          </div>
          <ol className="mt-16 grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-5">
            {f.cycle.map((step, i) => (
              <li key={step} className="bg-card px-5 py-4">
                <span className="font-mono text-[10px] text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                <p className="mt-1 text-sm font-semibold text-foreground">{step}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section-padding section-band-alt">
        <div className="enterprise-container">
          <Eyebrow>{feats.eyebrow}</Eyebrow>
          <h2 className="section-title mb-12 max-w-2xl text-foreground">{feats.title}</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {feats.items.map((item, i) => {
              const Icon = featureIcons[i] ?? FileText;
              return (
                <div key={item.title} className="rounded-2xl border border-border bg-card p-6 transition-colors hover:border-primary/40">
                  <span className="flex size-10 items-center justify-center rounded-xl bg-primary/10">
                    <Icon className="size-5 text-primary" aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 font-semibold text-foreground">{item.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{item.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-padding section-band-base">
        <div className="enterprise-container">
          <Eyebrow>{steps.eyebrow}</Eyebrow>
          <h2 className="section-title mb-12 text-foreground">{steps.title}</h2>
          <ol className="relative grid gap-8 md:grid-cols-5">
            <div className="absolute left-0 right-0 top-5 hidden h-px bg-gradient-to-r from-primary/0 via-primary/60 to-primary/0 md:block" aria-hidden="true" />
            {steps.steps.map((s, i) => (
              <li key={s.title} className="relative">
                <span className="relative flex size-10 items-center justify-center rounded-full border border-primary bg-background font-mono text-sm text-primary">
                  {i + 1}
                </span>
                <h3 className="mt-4 font-semibold text-foreground">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
              </li>
            ))}
          </ol>
          <Link to={FONDEA_WAITLIST_HREF} className="mt-12 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">
            {steps.cta} <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        </div>
      </section>

      <section className="py-16 section-band-alt">
        <div className="enterprise-container grid gap-10 lg:grid-cols-[1fr_1.2fr] lg:items-center">
          <div>
            <Eyebrow>{fund.eyebrow}</Eyebrow>
            <h2 className="font-display text-2xl font-semibold text-foreground md:text-3xl">{fund.title}</h2>
            <p className="mt-4 text-muted-foreground">{fund.body}</p>
            <Link to={FONDEA_WAITLIST_HREF} className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
              {fund.cta} <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </div>
          <ul className="flex flex-wrap gap-2">
            {fund.items.map((item) => (
              <li key={item} className="rounded-full border border-border bg-card px-4 py-2 text-sm text-foreground">
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
};
